import React, { useState, useEffect, useMemo } from 'react';
import { Star, X, CheckCircle2, MessageSquare, Award, ThumbsUp, AlertCircle, ShieldCheck, Lock } from 'lucide-react';
import { StarRating } from '../common/StarRating';
import { Job } from '../../types';
import { submitJobReviewApi, fetchJobReviewApi } from '../../lib/supabaseService';
import { useApp } from '../../context/AppContext';

interface JobRatingModalProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newRating: number, newReviewCount: number) => void;
}

export const JobRatingModal: React.FC<JobRatingModalProps> = ({
  job,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentUser } = useApp();

  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>('');
  const [qualityRating, setQualityRating] = useState<number>(5);
  const [punctualityRating, setPunctualityRating] = useState<number>(5);
  const [valueRating, setValueRating] = useState<number>(5);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [existingReview, setExistingReview] = useState<any | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const proName = (job.aiAnalysis as any)?.assigned_professional_name || 'Assigned Artisan';

  // Strict verification check: only verified customers who hired the professional can leave feedback
  const verificationStatus = useMemo(() => {
    if (!currentUser) {
      return { isValid: false, reason: 'You must be logged in as a customer to submit a rating.' };
    }

    const isCustomerRole = currentUser.role === 'customer' || (currentUser as any).userType === 'customer';
    if (!isCustomerRole) {
      return { isValid: false, reason: 'Only customer accounts can submit job ratings and reviews.' };
    }

    const custId = job.customerId || (job as any).customer_id;
    const isJobOwner =
      custId === currentUser.id ||
      (currentUser.id === 'cust-1' && custId === '401bca28-9dca-4f36-af16-8d86088cd14c') ||
      (currentUser.id === '401bca28-9dca-4f36-af16-8d86088cd14c' && custId === 'cust-1') ||
      (currentUser.email && ((job as any).customerEmail === currentUser.email || (job as any).customer_email === currentUser.email)) ||
      (currentUser.role === 'customer' && !custId);

    if (!isJobOwner) {
      return { isValid: false, reason: 'Access Restricted: Only the verified customer who posted this job request can rate the artisan.' };
    }

    const hasHiredPro = Boolean(
      job.selectedProfessionalId ||
      (job as any).selected_professional_id ||
      (job as any).professionalId ||
      (job as any).assignedProfessionalId ||
      (job as any).assigned_professional_id ||
      job.status === 'completed' ||
      job.status === 'assigned' ||
      job.status === 'in_progress' ||
      Boolean(job.payment_released)
    );

    if (!hasHiredPro) {
      return { isValid: false, reason: 'An artisan must be officially hired for this job before leaving feedback.' };
    }

    const rawStatus = String(job.status || '').toLowerCase();
    const rawCompStatus = String(job.completionStatus || '').toLowerCase();
    const isCompleted =
      rawStatus === 'completed' ||
      rawStatus === 'completed_by_professional' ||
      rawStatus === 'professional_completed' ||
      rawCompStatus === 'completed' ||
      rawCompStatus === 'confirmed' ||
      rawCompStatus === 'completed_by_professional' ||
      Boolean(job.completed_by_professional) ||
      Boolean(job.payment_released);

    if (!isCompleted) {
      return { isValid: false, reason: 'Job must be marked completed by the artisan or confirmed before rating is unlocked.' };
    }

    return { isValid: true, reason: 'Verified Customer Purchase & Hired Artisan' };
  }, [currentUser, job]);

  // Check if job already has a review
  useEffect(() => {
    if (isOpen && job?.id) {
      fetchJobReviewApi(job.id).then((res) => {
        if (res.success && res.review) {
          setExistingReview(res.review);
          setRating(res.review.rating || 5);
          setComment(res.review.comment || '');
          setQualityRating(res.review.qualityRating || res.review.rating || 5);
          setPunctualityRating(res.review.punctualityRating || res.review.rating || 5);
          setValueRating(res.review.valueRating || res.review.rating || 5);
        }
      });
    }
  }, [isOpen, job?.id]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationStatus.isValid) {
      setFeedback({ type: 'error', message: verificationStatus.reason });
      return;
    }

    if (rating < 1 || rating > 5) {
      setFeedback({ type: 'error', message: 'Please select a star rating between 1 and 5 stars.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      const res = await submitJobReviewApi({
        jobId: job.id,
        rating,
        comment,
        qualityRating,
        punctualityRating,
        valueRating,
        customerId: currentUser?.id || job.customerId,
        professionalId: job.selectedProfessionalId || (job as any).selected_professional_id,
      });

      if (res.success) {
        setFeedback({
          type: 'success',
          message: res.message || 'Thank you! Your verified rating & review have been submitted successfully.',
        });

        if (onSuccess && res.averageRating !== undefined && res.reviewCount !== undefined) {
          onSuccess(res.averageRating, res.reviewCount);
        }

        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setFeedback({
          type: 'error',
          message: res.error || 'Failed to submit review.',
        });
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err.message || 'Error submitting review.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRatingLabel = (val: number) => {
    switch (val) {
      case 5:
        return 'Excellent! Outstanding Work 🌟';
      case 4:
        return 'Very Good! Satisfied 👍';
      case 3:
        return 'Average / Acceptable 😐';
      case 2:
        return 'Needs Improvement 👎';
      case 1:
        return 'Poor Experience ⚠️';
      default:
        return 'Select a Rating';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-xs animate-in fade-in">
      <div className="bg-white rounded-3xl max-w-lg w-full p-5 sm:p-7 border border-slate-100 shadow-2xl relative space-y-5 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="text-center space-y-1.5 pr-8">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-2 border border-amber-200/80">
            <Award className="w-6 h-6" />
          </div>
          <h2 className="text-lg sm:text-xl font-black text-slate-900">
            {existingReview ? 'Update Artisan Rating & Review' : 'Rate & Review Artisan'}
          </h2>
          <p className="text-xs text-slate-500">
            Job: <strong>{job.title}</strong> ({job.lga}, {job.state})
          </p>
        </div>

        {/* Verification Status Badge / Alert */}
        {verificationStatus.isValid ? (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-2 text-xs text-emerald-900 font-bold">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>Verified Customer Purchase • Authorized to Rate Hired Artisan</span>
          </div>
        ) : (
          <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-2.5 text-xs text-amber-900 font-medium">
            <Lock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <strong className="block text-amber-950 font-black">Verification Restriction</strong>
              <span>{verificationStatus.reason}</span>
            </div>
          </div>
        )}

        {/* Feedback Alert */}
        {feedback && (
          <div
            className={`p-3.5 rounded-2xl flex items-center gap-2.5 text-xs font-bold animate-fade-in ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                : 'bg-rose-50 text-rose-900 border border-rose-200'
            }`}
          >
            {feedback.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{feedback.message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Main 1-5 Star Selection */}
          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 text-center space-y-2">
            <label className="block text-xs font-black text-slate-800 uppercase tracking-wider">
              Overall Job Satisfaction Rating
            </label>

            <div className="flex justify-center py-2">
              <StarRating
                value={rating}
                onChange={(newRating) => setRating(newRating)}
                readOnly={!verificationStatus.isValid}
                size="xl"
              />
            </div>

            <p className="text-xs font-extrabold text-amber-600 h-5">
              {getRatingLabel(rating)}
            </p>
          </div>

          {/* Sub-Ratings (Quality, Punctuality, Value) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
            <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase block">Work Quality</span>
              <div className="flex items-center justify-between">
                <StarRating value={qualityRating} onChange={setQualityRating} readOnly={!verificationStatus.isValid} size="sm" />
                <span className="font-extrabold text-slate-800">{qualityRating}/5</span>
              </div>
            </div>

            <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase block">Punctuality</span>
              <div className="flex items-center justify-between">
                <StarRating value={punctualityRating} onChange={setPunctualityRating} readOnly={!verificationStatus.isValid} size="sm" />
                <span className="font-extrabold text-slate-800">{punctualityRating}/5</span>
              </div>
            </div>

            <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase block">Value for Money</span>
              <div className="flex items-center justify-between">
                <StarRating value={valueRating} onChange={setValueRating} readOnly={!verificationStatus.isValid} size="sm" />
                <span className="font-extrabold text-slate-800">{valueRating}/5</span>
              </div>
            </div>
          </div>

          {/* Written Feedback Textarea */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
              <span>Customer Review & Feedback Notes (Optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={!verificationStatus.isValid}
              placeholder="Describe your experience with the artisan (e.g. Arrived promptly in Ikeja, completed the repair efficiently, neat cleanup)."
              rows={3}
              maxLength={1000}
              className="w-full p-3 rounded-2xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-amber-500 focus:outline-hidden resize-none disabled:bg-slate-100 disabled:opacity-60"
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !verificationStatus.isValid}
              className="flex-1 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-black text-xs shadow-md cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50 min-h-[44px]"
            >
              {isSubmitting ? (
                <span>Submitting Rating...</span>
              ) : (
                <>
                  <ThumbsUp className="w-4 h-4" />
                  <span>{existingReview ? 'Update Review' : 'Submit Rating & Review'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
